// Überblick je Lieferant, Kennzahlen der Startseite und die Toleranzen des Einkaufs.
import { PERMISSION_AUTHENTICATED, PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { zSupplierRef, zUnitInfo } from './common'
import { zExpectedOrder } from './order'

export const supplierOverview = defineContract({
  name: 'purchasing.supplier.overview',
  title: 'Einkauf bei einem Lieferanten anzeigen',
  description: 'Offene Bestellungen, erwartete Lieferungen nach Tag, letzte Einkaufspreise und Abweichungen eines Lieferanten.',
  llm: {
    description:
      'Überblick über den Einkauf bei EINEM Lieferanten an den Standorten, die der Nutzer sehen darf: Konditionen ' +
      '(Mindestbestellwert, Lieferzeit, Bestell- und Liefertage), offene Bestellungen, erwartete Lieferungen je Tag, ' +
      'die zuletzt gültigen Einkaufspreise mit der Änderung zum vorherigen Preis (nur mit „Einstandspreise sehen“) und ' +
      'die Abweichungen der letzten 90 Tage aus Wareneingängen und Rechnungsprüfung. Verwenden für „Wie steht es mit ' +
      'dem Lieferanten?“. Stammdaten des Lieferanten: masterdata.supplier.get.',
    keywords: ['Lieferant', 'offene Bestellungen', 'erwartete Lieferungen', 'letzte Einkaufspreise', 'Abweichungen', 'Reklamationen', 'Lieferantenübersicht'],
  },
  input: z.strictObject({ supplierId: zId }),
  output: z.object({
    supplier: zSupplierRef,
    terms: z.object({
      leadTimeDays: z.int().nullable(),
      orderWeekdays: z.array(z.int()),
      deliveryWeekdays: z.array(z.int()),
      orderChannel: z.string().nullable(),
      minOrderValueCents: z.int().nullable().optional(),
    }),
    counts: z.object({ openOrders: z.int(), waitingApproval: z.int(), drafts: z.int(), invoicesToCheck: z.int(), differences: z.int() }),
    openOrders: z.array(zExpectedOrder),
    /** erwartete Lieferungen der nächsten Wochen, je Tag */
    expected: z.array(z.object({ date: z.string().nullable(), orders: z.array(z.object({ id: zId, number: z.string().nullable(), locationCode: z.string(), summary: z.string() })) })),
    lastPrices: z.array(
      z.object({
        itemId: zId,
        sku: z.string(),
        name: z.string(),
        orderUnit: zUnitInfo,
        validFrom: z.string(),
        /** Cent netto je Bestelleinheit – nur mit „Einstandspreise sehen“ */
        priceCents: z.int().optional(),
        previousCents: z.int().nullable().optional(),
        /** Änderung zum vorherigen Preis in Basispunkten */
        changeBp: z.int().nullable().optional(),
      }),
    ),
    differences: z.array(
      z.object({
        /** receipt = Abweichung im Wareneingang · invoice = Abweichung in der Rechnungsprüfung */
        kind: z.enum(['receipt', 'invoice']),
        documentId: zId,
        documentType: z.string(),
        number: z.string().nullable(),
        date: z.string(),
        text: z.string(),
      }),
    ),
  }),
  permission: 'purchasing.order.read',
  risk: 'read',
  scope: 'any',
  fieldPermissions: {
    'terms.minOrderValueCents': PERMISSION_COSTS_READ,
    'openOrders.*.netCents': PERMISSION_COSTS_READ,
    'lastPrices.*.priceCents': PERMISSION_COSTS_READ,
    'lastPrices.*.previousCents': PERMISSION_COSTS_READ,
    'lastPrices.*.changeBp': PERMISSION_COSTS_READ,
  },
})

const zCountRow = z.object({ id: zId, title: z.string(), subtitle: z.string(), tone: z.enum(['neutral', 'warning', 'danger', 'success']).nullable() })

export const expectedToday = defineContract({
  name: 'purchasing.order.expectedToday',
  title: 'Heute erwartete Lieferungen',
  description: 'Bestellungen, deren Lieferung heute fällig oder überfällig ist.',
  llm: {
    description: 'Zählt und nennt die Bestellungen, deren Wunschtermin heute ist oder schon verstrichen ist und die noch nicht vollständig geliefert sind – an den Standorten des Nutzers. Für die ganze Liste purchasing.order.expected.',
    keywords: ['heute erwartet', 'Anlieferung heute', 'überfällige Lieferung'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int(), overdueCount: z.int(), rows: z.array(zCountRow) }),
  permission: 'purchasing.order.read',
  risk: 'read',
  scope: 'any',
})

export const ordersWaiting = defineContract({
  name: 'purchasing.order.waiting',
  title: 'Bestellungen, die auf Freigabe warten',
  description: 'Bestellungen im Zustand „Zur Freigabe“ an den Standorten des Nutzers.',
  llm: {
    description: 'Zählt und nennt Bestellungen, die auf Freigabe warten (über der Grenze der bestellenden Person). Freigeben: purchasing.order.approve.',
    keywords: ['Freigabe offen', 'wartet auf Freigabe', 'zu genehmigen'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int(), rows: z.array(zCountRow) }),
  permission: 'purchasing.order.read',
  risk: 'read',
  scope: 'any',
})

export const invoicesToCheck = defineContract({
  name: 'purchasing.invoice.toCheck',
  title: 'Eingangsrechnungen in Prüfung',
  description: 'Erfasste, noch nicht freigegebene Eingangsrechnungen.',
  llm: {
    description: 'Zählt und nennt Eingangsrechnungen, die noch geprüft und freigegeben werden müssen. Abgleich: purchasing.invoice.match.',
    keywords: ['Rechnungen zu prüfen', 'Rechnungsprüfung offen'],
  },
  input: z.strictObject({}),
  output: z.object({ count: z.int(), rows: z.array(zCountRow) }),
  permission: 'purchasing.invoice.read',
  risk: 'read',
  scope: 'any',
})

/** Zähler am Navigationseintrag „Bestellvorschläge“: offene Anforderungen der Filialen */
export const requisitionCount = defineContract({
  name: 'purchasing.requisition.count',
  title: 'Offene Anforderungen zählen',
  description: 'Zahl der offenen Anforderungen an den Einkauf.',
  llm: { description: 'Zählt die offenen Anforderungen der Standorte an den Einkauf. Einzeln: purchasing.requisition.list.', keywords: ['offene Anforderungen'] },
  input: z.strictObject({}),
  output: z.object({ count: z.int() }),
  permission: 'purchasing.suggestion.create',
  risk: 'read',
  scope: 'any',
})

export const zPurchasingSettings = z.object({
  /** so viel mehr als offen darf ein Wareneingang zu einer Bestellzeile buchen (Basispunkte der Bestellmenge) */
  overDeliveryBp: z.int(),
  /** Rechnungsprüfung: erlaubte Mehrmenge gegenüber der Lieferung (Basispunkte) */
  qtyToleranceBp: z.int(),
  /** Rechnungsprüfung: erlaubte Preisabweichung je Zeile – Basispunkte des erwarteten Betrags bzw. fester Betrag */
  priceToleranceBp: z.int(),
  priceToleranceCents: z.int(),
})
export type PurchasingSettings = z.output<typeof zPurchasingSettings>

export const settingsGet = defineContract({
  name: 'purchasing.settings.get',
  title: 'Toleranzen des Einkaufs anzeigen',
  description: 'Erlaubte Überlieferung und Toleranzen der Rechnungsprüfung.',
  llm: {
    description: 'Liefert die Toleranzen des Einkaufs: erlaubte Überlieferung beim Wareneingang zur Bestellung und die Mengen- und Preistoleranz der Rechnungsprüfung (Basispunkte, 100 = 1 %; fester Betrag in Cent).',
    keywords: ['Toleranz', 'Überlieferung', 'Preistoleranz'],
  },
  input: z.strictObject({}),
  output: zPurchasingSettings.extend({ editable: z.boolean() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})

const zBp = (label: string) =>
  z
    .int({ error: `${label}: Bitte eine Zahl eingeben.` })
    .min(0, { error: `${label} darf nicht negativ sein.` })
    .max(5000, { error: `${label}: höchstens 50 %.` })

export const settingsUpdate = defineContract({
  name: 'purchasing.settings.update',
  title: 'Toleranzen des Einkaufs ändern',
  description: 'Setzt die erlaubte Überlieferung und die Toleranzen der Rechnungsprüfung.',
  llm: {
    description: 'Ändert die Toleranzen des Einkaufs für den ganzen Betrieb. Nur die genannten Werte ändern sich. Basispunkte: 100 = 1 %. Wirkt auf künftige Wareneingänge und auf jede Rechnung, die noch in Prüfung ist.',
    keywords: ['Toleranz ändern', 'Überlieferung erlauben', 'Preistoleranz'],
  },
  input: z.strictObject({
    overDeliveryBp: zBp('Die Überlieferung').optional(),
    qtyToleranceBp: zBp('Die Mengentoleranz').optional(),
    priceToleranceBp: zBp('Die Preistoleranz').optional(),
    priceToleranceCents: z.int({ error: 'Bitte einen Betrag eingeben.' }).min(0, { error: 'Der Betrag darf nicht negativ sein.' }).max(1_000_000, { error: 'Der Betrag ist zu hoch.' }).optional(),
  }),
  output: zPurchasingSettings,
  permission: 'purchasing.settings.write',
  risk: 'financial',
})
